// src/components/SimpleFormComponents/SimpleEmail.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo } from 'react';
import { Form } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import { isValidEmail } from 'utils/helpers';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import ValidatedInput from 'components/JarvislyComponents/ValidatedInput';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleEmail = ({ name, subdocumentField, disabled, onBlur, prefix, ...props }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, form, disabledFields } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  const { customValidationStatus, customValidationMessage, handleChange } =
    useCustomValidationStatus({ ...props, ...elementProps }, isValidEmail);

  // read value from Form state so it reacts to typing, paste and programmatic setFieldsValue
  const currentValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const statusForSuffix = useMemo(() => {
    // empty value => no icon
    if (!currentValue) return '';

    // show validating if hook flagged it
    if (customValidationStatus === 'validating') return 'validating';

    // when there is a value:
    // error if invalid, success if valid
    return isValidEmail(currentValue) ? 'success' : 'error';
  }, [currentValue, customValidationStatus]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
      // keep AntD feedback off; we use our static suffix
      showFeedback={false}
    >
      <ValidatedInput
        ref={elementRef}
        id={flattenFieldName}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={isDisabled}
        status={statusForSuffix}
        loading={false}
        prefix={prefix}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleEmail;

// *******************************************************************************
// HELPERS
// *******************************************************************************
